import { createAppRouter, initGuards } from '@/router';
import { createStore } from '@/store/store';
import { AppState } from '@/store/types';
import domus, { HgSupportedLanguage, type SupportedPlatforms } from '@smg-real-estate/domus';
import { VueHeadClient, VueHeadMixin, createHead } from '@unhead/vue/client';
import { createSSRApp, type App, type Component } from 'vue';
import { type I18n } from 'vue-i18n';
import { type Router } from 'vue-router';
import { Store } from 'vuex';

export function createVueApp(
  appComponent: Component,
  i18n: I18n,
  platform: SupportedPlatforms,
): {
  app: App<Element>;
  router: Router;
  store: Store<AppState>;
  head: VueHeadClient;
} {
  const store = createStore();
  const head = createHead();
  // Create fresh router instance for each app (like ssr-test)
  // This ensures createWebHistory reads the current window.location.pathname correctly
  const router = createAppRouter();
  initGuards(router);

  const app = createSSRApp(appComponent);
  app.use(i18n);
  app.use(router);
  app.use(store);
  app.mixin(VueHeadMixin);
  app.use(head);

  // needs to be last as domus sets the locale when all translations are loaded
  app.use(domus, {
    theme: platform,
    i18n: { instance: i18n, languages: { name: 'all', mode: 'lazy' } },
    router: { instance: router, defaultLanguage: HgSupportedLanguage.DE },
  });

  return { app, router, store, head };
}
